// What to tell the writer when a page can't be read or its copy can't be
// saved. Each message names the page and, where they can do something about
// it, says what. No page ids, no system wording.

/** Why a page that isn't on this device couldn't be downloaded. */
export type PageReadProblem =
  /** Still signing in, or the encryption key isn't unlocked yet. */
  | 'connecting'
  | 'offline'
  /** Sync turned off on this device. */
  | 'syncStopped'
  /** The sync subscription has ended. */
  | 'syncLapsed'
  /** The device it was written on hasn't uploaded it yet. */
  | 'notUploaded'
  | 'timedOut'
  | 'downloadFailed'

/** What the reader was doing, as "can't be duplicated" / "then duplicate it". */
export interface PageAction {
  done: string
  base: string
}

const READING: PageAction = { done: 'read', base: 'read it' }
export const DUPLICATING: PageAction = { done: 'duplicated', base: 'duplicate it' }
export const EXPORTING: PageAction = { done: 'exported', base: 'export it' }

const quoted = (name: string) => `“${name}”`

export function pageReadMessage(problem: PageReadProblem, name: string, action: PageAction = READING): string {
  const page = quoted(name)
  switch (problem) {
    case 'connecting':
      return `${page} can’t be ${action.done} yet. This device is still connecting to your account. Try again in a moment.`
    case 'offline':
      return `${page} hasn’t been downloaded to this device yet, so it can’t be ${action.done} while you’re offline.`
    case 'syncStopped':
      return `${page} hasn’t been downloaded to this device yet. Turn sync back on to download it, then ${action.base}.`
    case 'syncLapsed':
      return `${page} hasn’t been downloaded to this device yet, and your sync subscription has ended, so it can’t be downloaded.`
    case 'notUploaded':
      return `${page} hasn’t reached the cloud yet from the device it was written on, so it can’t be ${action.done} here yet.`
    case 'timedOut':
      return `${page} couldn’t be downloaded. The server didn’t answer in time. Try again in a moment.`
    case 'downloadFailed':
      return `${page} couldn’t be downloaded. Try again in a moment.`
  }
}

/** Storage errors that mean the device (or the browser's share of it) is full. */
const FULL = /QuotaExceeded|quota|No space left|ENOSPC|os error 28|os error 112|disk (is )?full|not enough space/i

export function isStorageFull(error: unknown): boolean {
  const text = error instanceof Error ? `${error.name} ${error.message}` : String(error ?? '')
  return FULL.test(text)
}

/** Saving a page's copy failed. */
export function copySaveMessage(error: unknown, name: string): string {
  return isStorageFull(error)
    ? `There isn’t enough space on this device to duplicate ${quoted(name)}. Free some space, then try again.`
    : `${quoted(name)} couldn’t be duplicated because the copy couldn’t be saved on this device.`
}
